/**
 * Trailblazers (TRL) galaxy-map topology
 *
 * Pure graph functions over the authored node/edge graph. They run in the
 * browser (admin builder + player client) AND in Node (server gating) so every
 * layer derives identical round "levels" and validation from the same source.
 *
 * The map is a directed acyclic graph (DAG):
 * - Direction flows from starting planets toward a single final planet.
 * - A node's "level" is its longest-path depth from any start (start = 1).
 * - Start nodes have in-degree 0; the final node has out-degree 0.
 *
 * These functions accept minimal structural shapes (id + endpoints + image
 * count) rather than the full DTOs, so callers can pass either the persisted
 * {@link TrlMapDef} nodes/edges or the in-flight builder state.
 */
function buildAdjacency(nodes, edges) {
    const out = new Map();
    const inn = new Map();
    const inDegree = new Map();
    const outDegree = new Map();
    for (const n of nodes) {
        out.set(n.id, []);
        inn.set(n.id, []);
        inDegree.set(n.id, 0);
        outDegree.set(n.id, 0);
    }
    for (const e of edges) {
        // Ignore edges that reference missing nodes — validation reports those.
        if (!out.has(e.fromNodeId) || !inn.has(e.toNodeId))
            continue;
        out.get(e.fromNodeId).push(e.toNodeId);
        inn.get(e.toNodeId).push(e.fromNodeId);
        outDegree.set(e.fromNodeId, (outDegree.get(e.fromNodeId) ?? 0) + 1);
        inDegree.set(e.toNodeId, (inDegree.get(e.toNodeId) ?? 0) + 1);
    }
    return { out, in: inn, inDegree, outDegree };
}
// ============================================================================
// Start / final / next
// ============================================================================
/** Ids of starting nodes (in-degree 0). These are the round-1 options. */
export function getStartNodes(nodes, edges) {
    const adj = buildAdjacency(nodes, edges);
    return nodes.filter((n) => (adj.inDegree.get(n.id) ?? 0) === 0).map((n) => n.id);
}
/** Ids of final nodes (out-degree 0). A valid map has exactly one. */
export function getFinalNodes(nodes, edges) {
    const adj = buildAdjacency(nodes, edges);
    return nodes.filter((n) => (adj.outDegree.get(n.id) ?? 0) === 0).map((n) => n.id);
}
/** The single final node id, or undefined if not exactly one exists. */
export function getFinalNode(nodes, edges) {
    const finals = getFinalNodes(nodes, edges);
    return finals.length === 1 ? finals[0] : undefined;
}
/** Ids of the nodes revealed when `nodeId` is cleared (its successors). */
export function getNextNodes(nodeId, edges) {
    return edges.filter((e) => e.fromNodeId === nodeId).map((e) => e.toNodeId);
}
// ============================================================================
// Level derivation (longest-path depth from any start)
// ============================================================================
/**
 * Derive each node's level via longest-path in topological order.
 * Start nodes = level 1; a node's level = 1 + max(predecessor levels).
 *
 * Returns a map of node id → level. If the graph contains a cycle, the nodes
 * caught in the cycle are omitted (they can never be topologically ordered);
 * callers should run {@link validateMap} first to reject cyclic maps.
 */
export function deriveNodeLevels(nodes, edges) {
    const adj = buildAdjacency(nodes, edges);
    const level = new Map();
    // Kahn's algorithm, propagating longest-path levels as we peel nodes.
    const remainingIn = new Map();
    const queue = [];
    for (const n of nodes) {
        const deg = adj.inDegree.get(n.id) ?? 0;
        remainingIn.set(n.id, deg);
        if (deg === 0) {
            level.set(n.id, 1);
            queue.push(n.id);
        }
    }
    while (queue.length > 0) {
        const id = queue.shift();
        const myLevel = level.get(id) ?? 1;
        for (const next of adj.out.get(id) ?? []) {
            // A successor's level is the max over all its predecessors' levels + 1.
            const candidate = myLevel + 1;
            if (candidate > (level.get(next) ?? 0))
                level.set(next, candidate);
            remainingIn.set(next, (remainingIn.get(next) ?? 0) - 1);
            if ((remainingIn.get(next) ?? 0) === 0)
                queue.push(next);
        }
    }
    return level;
}
/**
 * A team's current round: the highest derived level among the nodes it has
 * cleared, plus its active (currently-travelling) node. 0 before it clears
 * anything.
 *
 * Traversal is forward-only — a team may only travel to a node whose level is
 * strictly greater than this. Because a team clears a single chain through the
 * DAG, every genuine forward frontier is a successor of its deepest cleared node
 * and so always sits at a strictly later round; anything at this level or below
 * is a skipped sibling / earlier-round planet the team can no longer pick.
 *
 * Any future mechanic that needs to relax this forward-only rule should gate the
 * exception here rather than loosening the per-call checks in the client/server.
 */
export function getTeamProgressLevel(nodes, edges, clearedNodeIds, currentNodeId) {
    const levels = deriveNodeLevels(nodes, edges);
    let max = 0;
    for (const id of clearedNodeIds)
        max = Math.max(max, levels.get(id) ?? 0);
    if (currentNodeId)
        max = Math.max(max, levels.get(currentNodeId) ?? 0);
    return max;
}
/**
 * The distinct levels at which checkpoint nodes sit, ascending. Used to schedule
 * the checkpoint barriers (all teams must reach a checkpoint level before any
 * team proceeds past it).
 */
export function getCheckpointLevels(nodes, edges) {
    const levels = deriveNodeLevels(nodes, edges);
    const set = new Set();
    for (const n of nodes) {
        if (n.isCheckpoint) {
            const lvl = levels.get(n.id);
            if (lvl !== undefined)
                set.add(lvl);
        }
    }
    return Array.from(set).sort((a, b) => a - b);
}
/**
 * Checkpoint barrier rule — single source of truth for the server (which holds
 * and releases teams) and the client (which shows "N of M teams arrived").
 *
 * A team that clears a checkpoint planet is held at that planet's level. The
 * barrier opens once EVERY team has reached that round: it has cleared a planet
 * at that level or deeper, or has already finished. Map validation guarantees
 * every planet in a checkpoint round is a checkpoint and no path skips the
 * round, so "cleared a planet in the round" always means "held at a checkpoint".
 *
 * `roundsCleared` is the deepest level among a team's cleared planets (see
 * {@link getTeamProgressLevel} without a current node) — safe to compute from
 * the masked snapshot, which carries it for rival teams too.
 */
export function hasTeamReachedCheckpointLevel(team, checkpointLevel) {
    return team.finished || team.roundsCleared >= checkpointLevel;
}
/** Whether the checkpoint barrier at `checkpointLevel` is open for the whole field. */
export function isCheckpointBarrierOpen(teams, checkpointLevel) {
    return teams.every((t) => hasTeamReachedCheckpointLevel(t, checkpointLevel));
}
/**
 * Con Clue fake-image rules, for planets that can hold a sabotage only. The fake
 * is required when Con Clue is enabled; when set it may never be one of the
 * planet's real images, and on a triangulation planet it must pass for the other
 * two — same planet, compatible faction — or the odd one out is an obvious tell.
 */
function validateDecoyImage(n, eligible, options) {
    const errors = [];
    if (!eligible)
        return errors;
    if (!n.decoyImageId) {
        if (options.requireDecoyImages) {
            errors.push({
                code: 'CON_CLUE_MISSING_DECOY',
                message: `"${n.id}" needs a Con Clue fake image (Con Clue sabotage is enabled).`,
                nodeId: n.id,
            });
        }
        return errors;
    }
    if ((n.imageIds ?? []).includes(n.decoyImageId)) {
        errors.push({
            code: 'CON_CLUE_DECOY_IS_REAL_IMAGE',
            message: `"${n.id}" uses one of its real images as its Con Clue fake.`,
            nodeId: n.id,
        });
    }
    if ((n.roundType ?? 'single') === 'triangulation') {
        const locs = (n.imageAreaKeys ?? []).filter(Boolean);
        if (n.decoyAreaKey && locs.length > 0 && locs.some((l) => l !== n.decoyAreaKey)) {
            errors.push({
                code: 'CON_CLUE_DECOY_MIXED_PLANET',
                message: `"${n.id}" has a Con Clue fake from a different planet than its triangulation images.`,
                nodeId: n.id,
            });
        }
        const factions = [...(n.imageFactions ?? []), n.decoyFaction].filter(Boolean);
        if (factions.includes('republic') && factions.includes('imperial')) {
            errors.push({
                code: 'CON_CLUE_DECOY_FACTION_CONFLICT',
                message: `"${n.id}" has a Con Clue fake whose faction conflicts with its triangulation images.`,
                nodeId: n.id,
            });
        }
    }
    return errors;
}
/**
 * Checkpoint rounds must be airtight, so the barrier holds every team exactly
 * once no matter which route it took:
 *
 * - CHECKPOINT_ROUND_MIXED: a round is either a checkpoint round or it isn't.
 *   If any planet in a round is a checkpoint, every planet in that round must be.
 * - CHECKPOINT_ROUND_SKIPPED: no path may jump over a checkpoint round (e.g. a
 *   round-2 planet linking straight to a round-4 planet past a round-3
 *   checkpoint), or a team taking that path would never be held.
 *
 * Levels are only well-defined on an acyclic graph, so callers skip this when a
 * cycle is present (already reported as CYCLE_DETECTED).
 */
function validateCheckpointRounds(nodes, edges) {
    const errors = [];
    const levels = deriveNodeLevels(nodes, edges);
    const checkpointLevels = getCheckpointLevels(nodes, edges);
    if (checkpointLevels.length === 0)
        return errors;
    const checkpointLevelSet = new Set(checkpointLevels);
    for (const n of nodes) {
        const level = levels.get(n.id);
        if (level !== undefined && checkpointLevelSet.has(level) && !n.isCheckpoint) {
            errors.push({
                code: 'CHECKPOINT_ROUND_MIXED',
                message: `"${n.id}" is in round ${level}, which is a checkpoint round — every planet in a checkpoint round must also be a checkpoint.`,
                nodeId: n.id,
            });
        }
    }
    for (const e of edges) {
        const from = levels.get(e.fromNodeId);
        const to = levels.get(e.toNodeId);
        if (from === undefined || to === undefined)
            continue;
        const skipped = checkpointLevels.find((level) => from < level && level < to);
        if (skipped !== undefined) {
            errors.push({
                code: 'CHECKPOINT_ROUND_SKIPPED',
                message: `The path from "${e.fromNodeId}" (round ${from}) to "${e.toNodeId}" (round ${to}) skips checkpoint round ${skipped} — teams on it would bypass the checkpoint.`,
                nodeId: e.fromNodeId,
            });
        }
    }
    return errors;
}
/**
 * Detect whether the graph contains a cycle, using Kahn's algorithm: if fewer
 * than all nodes get peeled, the remainder forms one or more cycles.
 */
function hasCycle(nodes, edges) {
    const adj = buildAdjacency(nodes, edges);
    const remainingIn = new Map();
    const queue = [];
    for (const n of nodes) {
        const deg = adj.inDegree.get(n.id) ?? 0;
        remainingIn.set(n.id, deg);
        if (deg === 0)
            queue.push(n.id);
    }
    let peeled = 0;
    while (queue.length > 0) {
        const id = queue.shift();
        peeled++;
        for (const next of adj.out.get(id) ?? []) {
            remainingIn.set(next, (remainingIn.get(next) ?? 0) - 1);
            if ((remainingIn.get(next) ?? 0) === 0)
                queue.push(next);
        }
    }
    return peeled < nodes.length;
}
/**
 * Validate a map's topology and image assignments. Returns `{ ok, errors[] }`.
 *
 * Rules:
 * - non-empty
 * - at least one start node (in-degree 0)
 * - exactly one final node (out-degree 0)
 * - acyclic
 * - no orphan nodes (isolated, when there is more than one node)
 * - every node has ≥1 image
 * - `imagesToClear` does not exceed the node's image count
 * - Con Clue fake-image rules on sabotage-eligible planets (see
 *   {@link validateDecoyImage}; required only with `options.requireDecoyImages`)
 * - Scanner Spy needs somewhere to go: at least one sabotage-eligible planet must
 *   be a triangulation planet (only with `options.requireTriangulationNode`)
 *
 * Note: a single-node map is degenerate — that node is both start and final,
 * which is allowed (it is not flagged as a dead-end).
 */
export function validateMap(nodes, edges, options = {}) {
    const errors = [];
    if (nodes.length === 0) {
        errors.push({ code: 'EMPTY_MAP', message: 'The map has no planets.' });
        return { ok: false, errors };
    }
    const adj = buildAdjacency(nodes, edges);
    const starts = nodes.filter((n) => (adj.inDegree.get(n.id) ?? 0) === 0);
    const finals = nodes.filter((n) => (adj.outDegree.get(n.id) ?? 0) === 0);
    if (starts.length === 0) {
        errors.push({ code: 'NO_START', message: 'No starting planet — every planet has an incoming connection (a cycle).' });
    }
    if (finals.length === 0) {
        errors.push({ code: 'NO_FINAL', message: 'No final planet — every planet has an outgoing connection (a cycle).' });
    }
    else if (finals.length > 1) {
        for (const f of finals) {
            errors.push({ code: 'MULTIPLE_FINAL', message: `"${f.id}" is a dead-end; there must be exactly one final planet.`, nodeId: f.id });
        }
    }
    if (hasCycle(nodes, edges)) {
        errors.push({ code: 'CYCLE_DETECTED', message: 'The map contains a cycle; connections must flow one way toward the final planet.' });
    }
    else {
        errors.push(...validateCheckpointRounds(nodes, edges));
    }
    // Orphans: a node with no connections at all, when the map has more than one node.
    if (nodes.length > 1) {
        for (const n of nodes) {
            const deg = (adj.inDegree.get(n.id) ?? 0) + (adj.outDegree.get(n.id) ?? 0);
            if (deg === 0) {
                errors.push({ code: 'ORPHAN_NODE', message: `"${n.id}" is not connected to any other planet.`, nodeId: n.id });
            }
        }
    }
    // Content checks per node — enforced per round type.
    let sabotageableTriangulationNodes = 0;
    for (const n of nodes) {
        const imageCount = n.imageCount ?? 0;
        const type = n.roundType ?? 'single';
        // A node can never require more images to clear than it actually has — that
        // would make it impossible to complete in-game. (Only meaningful once images
        // exist; an empty node is already reported as NODE_WITHOUT_IMAGE below.)
        if (imageCount > 0 && n.imagesToClear != null && n.imagesToClear > imageCount) {
            errors.push({
                code: 'IMAGES_TO_CLEAR_EXCEEDS_COUNT',
                message: `"${n.id}" needs ${n.imagesToClear} images to clear but only has ${imageCount}.`,
                nodeId: n.id,
            });
        }
        if (type === 'single') {
            if (imageCount === 0) {
                errors.push({ code: 'NODE_WITHOUT_IMAGE', message: `"${n.id}" has no screenshot assigned.`, nodeId: n.id });
            }
            else if (imageCount !== 1) {
                errors.push({
                    code: 'SINGLE_NODE_WRONG_IMAGE_COUNT',
                    message: `"${n.id}" (Single) must have exactly 1 image but has ${imageCount}.`,
                    nodeId: n.id,
                });
            }
        }
        else {
            // triangulation
            if (imageCount === 0) {
                errors.push({ code: 'NODE_WITHOUT_IMAGE', message: `"${n.id}" has no screenshots assigned.`, nodeId: n.id });
            }
            else if (imageCount !== 3) {
                errors.push({
                    code: 'TRIANGULATION_NODE_WRONG_IMAGE_COUNT',
                    message: `"${n.id}" (Triangulation) must have exactly 3 images but has ${imageCount}.`,
                    nodeId: n.id,
                });
            }
            if (imageCount === 3) {
                const locs = (n.imageAreaKeys ?? []).filter(Boolean);
                if (locs.length === 3 && new Set(locs).size > 1) {
                    errors.push({
                        code: 'TRIANGULATION_MIXED_PLANETS',
                        message: `"${n.id}" has triangulation images from different planets; all 3 must be from the same area.`,
                        nodeId: n.id,
                    });
                }
                const factions = (n.imageFactions ?? []).filter(Boolean);
                const hasRepublicOnly = factions.some((f) => f === 'republic');
                const hasImperialOnly = factions.some((f) => f === 'imperial');
                if (hasRepublicOnly && hasImperialOnly) {
                    errors.push({
                        code: 'TRIANGULATION_FACTION_CONFLICT',
                        message: `"${n.id}" mixes Republic-only and Imperial-only images; incompatible factions.`,
                        nodeId: n.id,
                    });
                }
            }
            if (!n.hasTriangulationTarget) {
                errors.push({
                    code: 'TRIANGULATION_MISSING_TARGET',
                    message: `"${n.id}" (Triangulation) has no target coordinates set for the final bury location.`,
                    nodeId: n.id,
                });
            }
        }
        // Start = in-degree 0, final = out-degree 0 — neither can hold a sabotage.
        const eligible = (adj.inDegree.get(n.id) ?? 0) > 0 && (adj.outDegree.get(n.id) ?? 0) > 0;
        if (eligible && type === 'triangulation')
            sabotageableTriangulationNodes += 1;
        errors.push(...validateDecoyImage(n, eligible, options));
    }
    // Scanner Spy acts on a planet's individual screenshots, so it can only be
    // placed on a triangulation planet. With none available the sabotage is enabled
    // but unplaceable, which reads to captains as a broken lobby.
    if (options.requireTriangulationNode && sabotageableTriangulationNodes === 0) {
        errors.push({
            code: 'SCANNER_SPY_NO_TRIANGULATION_NODE',
            message: 'Scanner Spy sabotage is enabled, but no planet can hold one — it needs a Triangulation planet that is neither the start nor the final planet.',
        });
    }
    return { ok: errors.length === 0, errors };
}
